/**
 * Session-scoped booking-source attribution.
 *
 * Rules (see PRD):
 *   - Default source = "marketplace".
 *   - Only "direct" when the user ENTERED THIS SESSION via a
 *     provider-specific URL (`/@username`).
 *   - Only "referral" when the entry URL carried `?ref=<code>`.
 *   - Discover / Search / Category landings always keep "marketplace".
 *   - Source is locked at the first page-load of a browser session
 *     (sessionStorage). No downstream page can flip it. Opening a
 *     new browser window starts a fresh session and re-captures.
 *
 * Why sessionStorage rather than a cookie?
 *   The previous implementation used a 30-day cookie. A user who
 *   visited `/@brand` once (e.g. from Instagram) and came back a
 *   week later through Discovery still had that cookie, so their
 *   booking was mis-attributed as "direct" and no commission was
 *   charged. Session-scoped storage gives each visit a clean slate.
 *
 * We also write the SAME values to `localStorage.last_*` purely for
 * UI niceties (referral credit display, etc.), they must never drive
 * the commission decision.
 */

const KEY_SOURCE = 'zuriya_booking_source';
const KEY_PROVIDER = 'zuriya_booking_source_provider';
const KEY_REF = 'zuriya_booking_source_ref';

function _safeGet(storage, key) {
  try { return storage.getItem(key); } catch { return null; }
}
function _safeSet(storage, key, value) {
  try { storage.setItem(key, value); } catch { /* private mode, ignore */ }
}

export function captureBookingSource({ pathname, search } = {}) {
  // If already captured this session, do nothing (locked for the whole session).
  if (_safeGet(sessionStorage, KEY_SOURCE)) return;

  // Stale cookies from the previous cookie-based implementation used a
  // 30-day lifetime which is how discovery bookings were being mis-
  // attributed as "direct". Clear them on first capture of this session
  // so they can't influence anything.
  try {
    document.cookie = 'zuriya_source=; path=/; max-age=0; SameSite=Lax';
    document.cookie = 'zuriya_source_provider=; path=/; max-age=0; SameSite=Lax';
  } catch { /* SSR / restricted contexts */ }

  const path = pathname || (typeof window !== 'undefined' ? window.location.pathname : '');
  const query = search || (typeof window !== 'undefined' ? window.location.search : '');

  // Provider direct-share link: /@username (allow dots + underscores).
  const directMatch = path.match(/^\/@([\w.]+)/);
  if (directMatch) {
    const username = directMatch[1];
    _safeSet(sessionStorage, KEY_SOURCE, 'direct');
    _safeSet(sessionStorage, KEY_PROVIDER, username);
    _safeSet(localStorage, 'zuriya_last_direct_provider', username);
    return;
  }

  // Referral code in query string.
  const params = new URLSearchParams(query);
  const refCode = params.get('ref') || params.get('referral');
  if (refCode) {
    _safeSet(sessionStorage, KEY_SOURCE, 'referral');
    _safeSet(sessionStorage, KEY_REF, refCode);
    _safeSet(localStorage, 'zuriya_last_referral', refCode);
    return;
  }

  // Everything else (landing, /discovery, /search, /category/*, /login, etc.)
  // always anchors the session to marketplace.
  _safeSet(sessionStorage, KEY_SOURCE, 'marketplace');
}

/**
 * Read the session-locked source.
 *
 * `providerUsername` is only used to defensively demote to "marketplace"
 * when the stored direct-link is for a DIFFERENT provider than the one
 * the client is now booking, so a user who lands via /@brandA but ends
 * up booking brandB (e.g. clicked through Related Providers) still
 * gets the correct commission.
 */
export function getBookingSource(providerUsername) {
  // Ensure we've captured something for this session. Safe to call
  // repeatedly, it's a no-op after the first run.
  captureBookingSource();

  const raw = _safeGet(sessionStorage, KEY_SOURCE) || 'marketplace';
  if (raw === 'direct') {
    const sourceProvider = _safeGet(sessionStorage, KEY_PROVIDER);
    // Demote to marketplace when the booked provider is NOT the
    // one whose direct link started this session. Do NOT accept
    // the "no sourceProvider stored" case as direct, that's how
    // the old bug let stray cookies mis-attribute bookings.
    if (sourceProvider && providerUsername && sourceProvider === providerUsername) {
      return 'direct';
    }
    return 'marketplace';
  }
  if (raw === 'referral') return 'referral';
  return 'marketplace';
}

/** Read any referral code captured at session entry (may be null). */
export function getReferralCode() {
  return _safeGet(sessionStorage, KEY_REF);
}
